import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import GlobalStyles from 'src/components/GlobalStyles';
import ScrollReset from 'src/components/ScrollReset';
import { AuthProvider } from 'src/contexts/AuthContext';
import useSettings from 'src/hooks/useSettings';
import AppRoutes from 'src/routes';
import { createTheme } from 'src/theme';
import { ClientViewProvider } from './contexts/ClientViewContext';
import { HashReferenceProvider } from './contexts/HashReferenceContext';
import { ClientsProvider } from './contexts/ClientsContext';
import { ParentClientCacheProvider } from './contexts/ParentClientCacheContext';
import { RetailerCacheProvider } from './contexts/RetailerCacheContext';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './AppInsights';
import Rocketize from 'src/components/Rocketize';

const App = () => {
  const { settings } = useSettings();
  const notistackRef = React.createRef();
  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* I added pointerEvents according to a github issue that made buttons unclickable
              https://github.com/iamhosseindhv/notistack/issues/394 */}
            <SnackbarProvider
              dense
              maxSnack={5}
              style={{ pointerEvents: 'all' }}
              ref={notistackRef}
              action={(key) => (
                <Button color="inherit" onClick={onClickDismiss(key)}>
                  Dismiss
                </Button>
              )}
            >
              <ClientsProvider>
                <RetailerCacheProvider>
                  <ParentClientCacheProvider>
                    <ClientViewProvider>
                      <HashReferenceProvider>
                        <AuthProvider>
                          <BrowserRouter>
                            <GlobalStyles />
                            <ScrollReset />
                            <AppRoutes />
                            <Rocketize active />
                          </BrowserRouter>
                        </AuthProvider>
                      </HashReferenceProvider>
                    </ClientViewProvider>
                  </ParentClientCacheProvider>
                </RetailerCacheProvider>
              </ClientsProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </AppInsightsContext.Provider>
  );
};

export default App;
